/**
 * Problems service — FHIR Condition-backed problem list + diagnoses.
 *
 * Problem list items (`category='problem-list-item'`) are longitudinal;
 * encounter diagnoses (`category='encounter-diagnosis'`) are visit-scoped
 * and can be promoted to the problem list via the `/promote` action.
 */

import { api } from './api';

export type ConditionClinicalStatus =
  | 'active' | 'recurrence' | 'relapse' | 'inactive' | 'remission' | 'resolved';

export type ConditionVerificationStatus =
  | 'unconfirmed' | 'provisional' | 'differential' | 'confirmed' | 'refuted' | 'entered-in-error';

export type ConditionCategory = 'problem-list-item' | 'encounter-diagnosis';

export interface PatientCondition {
  id: string;
  code: string;
  display: string;
  status: ConditionClinicalStatus;
  verificationStatus: ConditionVerificationStatus;
  severity?: string | null;
  category: ConditionCategory;
  onsetDate?: string | null;
  abatementDate?: string | null;
  recordedDate: string;
  recorder?: string | null;
  note?: string | null;
}

export interface SnomedConditionRef {
  code: string;
  display: string;
  icd10?: string;
  tags?: string[];
}

export interface CreateConditionPayload {
  snomedCode: string;
  display: string;
  clinicalStatus?: ConditionClinicalStatus;
  verificationStatus?: ConditionVerificationStatus;
  category?: ConditionCategory;
  severityCode?: string;
  severityDisplay?: string;
  onsetDate?: string;
  note?: string;
  // Encounter linkage — required by requireClinicalContext middleware
  // when the condition is attached to a visit.
  encounterId?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'direct_rx';
}

export const problemsService = {
  async getPatientConditions(
    clinicId: string,
    patientId: string,
    options?: { activeOnly?: boolean; status?: string },
  ): Promise<PatientCondition[]> {
    const params = new URLSearchParams();
    if (options?.activeOnly) params.append('activeOnly', 'true');
    if (options?.status) params.append('status', options.status);
    const qs = params.toString();
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/conditions${qs ? `?${qs}` : ''}`,
    );
    return response.data?.conditions || [];
  },

  async createPatientCondition(
    clinicId: string,
    patientId: string,
    payload: CreateConditionPayload,
  ): Promise<PatientCondition> {
    const body: CreateConditionPayload = {
      ...payload,
      off_encounter_reason:
        payload.off_encounter_reason
        || (payload.encounterId ? undefined : 'chart_correction'),
    };
    const response = await api.post(
      `/emr/${clinicId}/patients/${patientId}/conditions`,
      body,
    );
    return response.data || response;
  },

  async createEncounterDiagnosis(
    clinicId: string,
    encounterId: string,
    payload: Omit<CreateConditionPayload, 'encounterId' | 'off_encounter_reason' | 'category'>,
  ): Promise<PatientCondition> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/diagnoses`,
      payload,
    );
    return response.data || response;
  },

  async updateCondition(
    clinicId: string,
    conditionId: string,
    updates: Partial<{
      clinicalStatus: ConditionClinicalStatus;
      verificationStatus: ConditionVerificationStatus;
      severityCode: string;
      severityDisplay: string;
      abatementDate: string;
      note: string;
    }>,
  ): Promise<PatientCondition> {
    const response = await api.put(
      `/emr/${clinicId}/conditions/${conditionId}`,
      updates,
    );
    return response.data || response;
  },

  async resolveCondition(
    clinicId: string,
    conditionId: string,
    abatementDate?: string,
  ): Promise<PatientCondition> {
    const response = await api.post(
      `/emr/${clinicId}/conditions/${conditionId}/resolve`,
      { abatementDate },
    );
    return response.data || response;
  },

  async promoteToProblemList(
    clinicId: string,
    conditionId: string,
  ): Promise<PatientCondition> {
    const response = await api.post(
      `/emr/${clinicId}/conditions/${conditionId}/promote-to-problem-list`,
    );
    return response.data || response;
  },

  /**
   * Search the curated SNOMED CT diagnosis dictionary by free-text query.
   * Empty query returns the "common" tagged set first. Results are ranked
   * server-side (display-prefix > display-contains > code/icd > tag).
   */
  async searchSnomedConditions(query = '', limit = 20): Promise<SnomedConditionRef[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', String(limit));
    const response = await api.get(`/emr/snomed/conditions?${params.toString()}`);
    const data = response.data?.data || response.data;
    return data?.results || [];
  },
};

export default problemsService;
